import axios from "axios";
import { MessageBox } from "element-ui";
import { getToken, removeToken, removeImToken } from "@/utils/cookies";
import { apiUrl } from "@/config/env";
import i18n from "@/i18n/config";

export const api = axios.create({
	baseURL: apiUrl,
	timeout: 30000,
});

// 请求拦截器
api.interceptors.request.use(
	(config) => {
		const token = getToken();
		if (token) config.headers.Authorization = "Bearer " + token;

		config.headers.common.Accept = "application/x.sjlink.V2+json";

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// 响应拦截器
api.interceptors.response.use(
	(response) => {
		// 如果是 blob 类型，返回完整 response
		if (response.config.responseType === "blob") {
			return response;
		}

		if (response.data.status === 0) {
			return Promise.reject(response.data.message);
		}
		return response.data;
	},
	(error) => {
		if (!error.response) {
			return Promise.reject(i18n.t("apiJS.unknownMistake"));
		}

		if (error.response.status === 401 || error.response.data.message === "Unauthenticated.") {
			removeToken();
			removeImToken();
			MessageBox.alert(i18n.t("apiJS.loginHasExpired"), i18n.t("apiJS.hint"), {
				confirmButtonText: i18n.t("common.sure"),
				type: "warning",
			}).then(() => {
				location.reload();
			});
		} else if (error.config && error.config.preserveValidationError && (error.response.status === 422 || error.response.status === 404)) {
			return Promise.reject({
				...error.response.data,
				httpStatus: error.response.status,
			});
		} else if (error.response.status === 422) {
			const errors = Object.keys(error.response.data.errors).map((index) => error.response.data.errors[index]);
			return Promise.reject(errors[0][0]);
		} else if (error.response.data.message) {
			return Promise.reject(error.response.data.message);
		} else {
			return Promise.reject(i18n.t("apiJS.unknownMistake"));
		}
	}
);

// 获取对话列表
export const getConversations = () => {
	return api.get("/conversations");
};

// 获取联系人列表
export const getContacts = () => {
	return api.get("/contacts");
};

// 获取群组列表
export const getGroups = () => {
	return api.get("/groups");
};

// 获取推荐列表
export const getGeneralizations = (contactId, title) => {
	return api.get(`/promotion/${contactId}`, { params: { title } });
};

// 发送推荐消息
export const sendGeneralizationMessage = (data) => {
	return api.post("/promotion/sendPromotionMessages", data);
};

// 获取消息列表
export const getMessages = (contactId, groupId, page, perPage = 20, keyword) => {
	const params = {
		contact_id: contactId,
		group_id: groupId,
		page: page,
		per_page: perPage,
		keyword: keyword,
	};

	return api.get("/messages", { params: params });
};

//获取消息关联客户信息
export const getUserCustomers = (userId) => {
	return api.get(`user/${userId}/customers`);
};

// 发送消息
export const sendMessage = (contactId = null, groupId = null, type, text = null, attachment = null, length = null) => {
	const data = {
		type: type,
	};

	if (contactId) data.contact_id = contactId;
	if (groupId) data.group_id = groupId;
	if (text) data.text = text;
	if (attachment) data.attachment = attachment;
	if (length) data.length = length;

	return api.post("/messages", data);
};

// 获取oss签名，type=avatar,voice,picture
export const getSignature = (type) => {
	return api.post("/oss/signature", { type: type });
};

// 上传头像
export const updateAvatar = (filename) => {
	const data = {
		avatar: filename,
	};

	return api.put("/user/avatar", data);
};

// 获取群组信息
export const getGroup = (groupId) => {
	return api.get("/groups/" + groupId);
};

// 编辑群成员备注
export const putEditGroupMemberNote = (groupId, memberId, note) => {
	return api.put(`/groups/${groupId}/members/${memberId}/note`, {
		note: note,
	});
};

//获取联系人详情
export const getContactDetail = (userId) => {
	return api.get(`/contacts/${userId}/detail`);
};

// 修改组名
export const updateGroup = (groupId, groupName) => {
	return api.put(`/groups/${groupId}`, { name: groupName });
};

// 群邀请成员
export const inviteMembers = (groupId, membersId) => {
	return api.post(`/groups/${groupId}/invite`, { members: membersId });
};

// 踢出成员
export const kickMembers = (groupId, memberId) => {
	return api.post(`/groups/${groupId}/kick`, { members: memberId });
};

// 创建群聊
export const createGroup = (name, membersId) => {
	const data = {
		name: name,
		members: membersId,
	};

	return api.post("/groups", data);
};

// 获取群组消息阅读状态
export const getGroupReadStatus = (messageId) => {
	return api.get(`/messages/${messageId}/groupReadStatus`);
};

// 重置未读消息数和更新上次阅读时间
export const resetUnreadCountAndReportReadTime = (objectId) => {
	return api.post("/conversations/resetUnreadCountAndReportReadTime", { object_id: objectId });
};

// 撤回消息
export const recallMessage = (messageId) => {
	return api.post(`/messages/${messageId}/recall`);
};

//获取我的客户
export const getCustomers = () => {
	return api.get("/customers");
};

//获取单个客户电话绑定列表
export const getCustomersPhones = (customerId) => {
	return api.get(`/customers/${customerId}/phones`);
};

// 获取单个客户CRM电话绑定列表
export const getCustomersCRMPhones = (customerId) => {
	return api.get(`/customers/${customerId}/crm/phones`);
};

//绑定客户电话+备注+号码身份备注
export const bindPhone = (customerId, phoneNumber, comment, position) => {
	return api.post(`/customers/${customerId}/phones`, {
		phone_number: phoneNumber,
		comment: comment,
		position: position,
	});
};

// 解除电话绑定
export const unbindPhone = (customerId, phoneNumber) => {
	return api.delete(`/customers/${customerId}/phones/${phoneNumber}`, {
		customerId: customerId,
		phone_number: phoneNumber,
	});
};

// 编辑绑定客户电话号码
export const editPhone = (customerId, phoneNumber, comment, position) => {
	return api.put(`/customers/${customerId}/phones/${phoneNumber}`, {
		comment: comment,
		position: position,
	});
};

//获取客户所有商品
export const getCustomerPorduct = (customerId, currentPage, keyword) => {
	const params = {
		keyword: keyword,
		page: currentPage,
	};
	return api.get(`customers/${customerId}/products`, {
		params: params,
	});
};

//获取客户历史商品
export const getCustomerHistoryPorduct = (customerId) => {
	return api.get(`customers/${customerId}/products/history`);
};

//从其他客户导入history
export const postImportHistoryByCustomer = (customerId, referenceCustomerId, productIds) => {
	return api.post(`customers/${customerId}/products/importHistoryByCustomerId`, {
		reference_customer_id: referenceCustomerId,
		product_ids: productIds,
	});
};

//商品插入history
export const putImportHistory = (customerId, productId) => {
	return api.put(`customers/${customerId}/products/${productId}/history`);
};

//编辑客户商品
export const editCustomerProduct = (customerId, productId, alias, status, attributeOptions) => {
	return api.put(`customers/${customerId}/products/${productId}`, {
		alias: alias,
		attribute_options: attributeOptions,
		status: status,
	});
};

//获取规格自定义属性列表
export const getCustomerAttributes = (customerId) => {
	return api.get(`customers/${customerId}/attributes`);
};

//删除规格自定义属性
export const deleteCustomerAttributes = (customerId, attributeId) => {
	return api.delete(`customers/${customerId}/attributes/${attributeId}`);
};

//添加规格自定义属性
export const postCustomerAttributes = (customerId, attributes) => {
	return api.post(`customers/${customerId}/attributes`, attributes);
};

//获取规格自定义属性详情
export const getCustomerAttributesDetails = (customerId, attributeId) => {
	return api.get(`customers/${customerId}/attributes/${attributeId}`);
};

//更新规格自定义属性
export const putCustomerAttributesUpdate = (customerId, attributeId, data) => {
	return api.put(`customers/${customerId}/attributes/${attributeId}`, {
		name: data.name,
		options: data.options,
	});
};

//获取客户商品同步数据
export const getCustomerPorductLastest = (customerId) => {
	return api.get(`customers/${customerId}/products/latest`);
};

//获取客户商品同步Count
export const getCustomerPorductLastestCount = (customerId, status) => {
	return api.get(`customers/${customerId}/products/latest/status/${status}/count`);
};

//客户商品同步应用
export const postCustomerApplyProductLastest = (customerId, productId) => {
	return api.post(`customers/${customerId}/products/${productId}/latest/apply`);
};

//客户商品同步不应用
export const postCustomerAbortProductLastest = (customerId, productId) => {
	return api.post(`customers/${customerId}/products/${productId}/latest/abort`);
};

//检查商品变动更新
export const postProductsCheckUpdate = (customerId) => {
	return api.post(`customers/${customerId}/products/latest/checkUpdate`);
};

// 订单详情
export const getOrderDetails = (orderId) => {
	return api.get(`orders/${orderId}`);
};

// 订单详情
export const getMerchantOrderDetails = (orderId) => {
	return api.get(`merchantOrders/${orderId}`);
};

//聊天置顶
export const putChatStick = (conversationId) => {
	return api.put(`conversations/${conversationId}/isTop`);
};

//聊天取消置顶
export const deleteChatStick = (conversationId) => {
	return api.delete(`conversations/${conversationId}/isTop`);
};

//聊天列表未读
export const putUnreadChat = (conversationId, num) => {
	return api.put(`conversations/${conversationId}/unreadCount`, {
		num: num,
	});
};

//查看聊天下一张图片
export const getNextPicture = (messageId, contactId, groupId) => {
	const params = {
		contact_id: contactId,
		group_id: groupId,
	};
	return api.get(`messages/${messageId}/nextPicture`, { params: params });
};

//查看聊天上一张图片
export const getPervPicture = (messageId, contactId, groupId) => {
	const params = {
		contact_id: contactId,
		group_id: groupId,
	};
	return api.get(`messages/${messageId}/previousPicture`, { params: params });
};

// 查看上下文（next）
export const getNextMessages = (messageId, contactId, groupId) => {
	const params = {
		contact_id: contactId,
		group_id: groupId,
	};
	return api.get(`messages/${messageId}/next`, { params: params });
};

// 查看上下文（previous）
export const getPreviousMessages = (messageId, contactId, groupId) => {
	const params = {
		contact_id: contactId,
		group_id: groupId,
	};
	return api.get(`messages/${messageId}/previous`, { params: params });
};

// 取支票详情
export const getCheques = (chequeId) => {
	return api.get(`cheques/${chequeId}`);
};

// 权限列表
export const getFeatures = (lang) => {
	return api.get("/features", {
		headers: {
			"X-localization": lang,
		},
	});
};

// 设置权限
export const postUpdateFeatureActive = (customerId, phoneNumber, featureId, active) => {
	return api.post(`customers/${customerId}/phones/${phoneNumber}/updateFeatureActive`, {
		feature_id: featureId,
		active: active,
	});
};

/**
 * 禁用客人sms黑名单功能
 * @param {*} customerId
 * @param {*} phoneNumber
 * @param {*} active
 */
export const postUpdateSmsBlacklistFeatureActive = (customerId, phoneNumber, active) => {
	return api.post(`customers/${customerId}/phones/${phoneNumber}/updateSmsBlacklistFeatureActive`, {
		active: active,
	});
};

// 设置bots AI开关状态
export const putBotsStatus = (userId, botId, status) => {
	return api.put(`user/${userId}/bots/${botId}/status`, {
		status: status,
	});
};

// 设置自动翻译开关状态conversations/{id}/autoTranslateStatus
export const putAutoTranslateStatus = (id, status) => {
	return api.put(`conversations/${id}/autoTranslateStatus`, {
		status: status,
	});
};

// 翻译文本messageId,
export const postTranslatedMessage = (messageId, text, language) => {
	return api.post("messages/translate", {
		message_id: messageId,
		text: text,
		target: language,
	});
};

// 显示隐藏翻译文本/api/admin/
export const putTranslatedShowStatus = (id, showStatus) => {
	return api.put(`messages/${id}/translated/showStatus`, {
		show_status: showStatus,
	});
};

//获取单个客户信息
export const getCustomersDetails = (customerId) => {
	return api.get(`/customers/${customerId}`);
};

// 获取短信发送列表
export const getMessageSendList = (customerId) => {
	return api.get(`/sms/unregisteredUsers`);
};

// 发送短信
export const postSendMessage = (data) => {
	return api.post(`/sms`, {
		...data,
	});
};

// 获取短信发送历史记录
export const getMessageSendHistroy = (phone_number) => {
	return api.get(`/sms/records`, { params: { phone_number } });
};

//批量更新商品别名
export const postbatchUpdateAliases = (formData) => {
	return api.post(`customers/products/batchUpdateAliases`, formData);
};

//下载模版
export const downloadTemplate = () => {
	return api.get(`customers/products/updateAliasesTemplateDownload`, {
		responseType: "blob",
	});
};

// 更新规格排序
export const updateCustomerAttributesSort = (customerId, data) => {
	return api.put(`customers/${customerId}/attributes/sort`, { attribute_ids: data });
};

// 获取用户联系人列表
export const getUserPhoneList = () => {
	return api.get(`merchant/phoneNumberCustomers`);
};

// 获取服务商维护列表
export const getServiceProviderMaintenanceList = () => {
	return api.get(`merchant/merchants`);
};

// 根据号码获取服务商维护状态
export const getUserPhoneMerchantList = (phoneNumber) => {
	return api.get(`merchant/userPhoneNumberMerchant`, {
		params: {
			phone_number: phoneNumber,
		},
	});
};

//批量更新服务商信息
export const postbatchUpdateServiceProviders = (data) => {
	return api.post(`merchant/userPhoneNumberMerchant/batch`, data);
};

//更新服务商状态
export const postServiceProviderStatus = (id, status, phoneNumber) => {
	return api.post(`merchant/userPhoneNumberMerchant`, {
		merchant_id: id,
		phone_number: phoneNumber,
		operation: status,
	});
};

//获取服务商上架消息阅读状态
export const getServiceProviderReadStatus = () => {
	return api.get(`merchant/listingHasBeenRead`);
};

//服务商上架消息已阅读记录
export const postServiceProviderReadStatus = () => {
	return api.post(`merchant/listingHasBeenRead`);
};

// 获取数字员工列表
export const getOpenClawUsers = () => {
	return api.get("openClaw/users");
};

// 更新数字员工访问状态
export const putOpenClawUserAccessStatus = (userId, accessStatus) => {
	return api.put(`openClaw/users/${userId}/accessStatus`, {
		access_status: accessStatus,
	});
};

const emailApiOptions = (options = {}) => {
	return {
		preserveValidationError: true,
		headers: {
			Accept: "application/json",
		},
		...options,
	};
};

// 获取当前销售的 AI 邮箱账号
export const getEmailAccount = () => {
	return api.get("email-account", emailApiOptions());
};

// 新增或更新 AI 邮箱账号；后端可能验证连接，因此延长超时
export const saveEmailAccount = (data) => {
	return api.post("email-account", data, emailApiOptions({ timeout: 60000 }));
};

// 启用或停用 AI 邮箱账号；重新启用时后端可能验证连接
export const updateEmailAccountStatus = (isEnabled) => {
	return api.patch("email-account/status", { is_enabled: isEnabled }, emailApiOptions({ timeout: 60000 }));
};

// 获取当前销售的 AI 邮件联系人列表
export const getEmailContacts = () => {
	return api.get("email-contacts", emailApiOptions());
};

// 新增 AI 邮件联系人
export const createEmailContact = (data) => {
	return api.post("email-contacts", data, emailApiOptions());
};

// 获取当前销售可关联的 Customer 选项
export const getEmailContactCustomers = () => {
	return api.get("email-contacts/customers", emailApiOptions());
};

// 获取单个 AI 邮件联系人详情
export const getEmailContact = (contactId) => {
	return api.get(`email-contacts/${contactId}`, emailApiOptions());
};

// 更新 AI 邮件联系人
export const updateEmailContact = (contactId, data) => {
	return api.put(`email-contacts/${contactId}`, data, emailApiOptions());
};

// 删除 AI 邮件联系人
export const deleteEmailContact = (contactId) => {
	return api.delete(`email-contacts/${contactId}`, emailApiOptions());
};
